<template>
  <div id="contenedor-recursos" class="bkground">
    <!-- Estado cargando -->
    <template v-if="$apollo.loading">
      <v-row>
        <v-col v-for="n in 4" :key="n" cols="12" md="3">
          <v-skeleton-loader type="card" height="100" class="rounded-lg" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="table" class="rounded-lg mt-4" />
    </template>

    <!-- Contenido -->
    <template v-else>
      <!-- Resumen financiero - Cards métricas -->
      <v-row class="mb-4">
        <!-- Recursos Autorizados -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg recursos-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-avatar size="42" color="primary" class="mr-3">
                  <v-icon color="white">mdi-bank</v-icon>
                </v-avatar>
                <div class="text-caption grey--text text-uppercase font-weight-medium">
                  Recursos Autorizados
                </div>
              </div>
              <div class="text-h5 font-weight-bold grey--text text--darken-3">
                {{ getTotalRecursosAutorizados | currency }}
              </div>
            </v-card-text>
            <div class="card-accent primary" />
          </v-card>
        </v-col>

        <!-- Total Contratado -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg recursos-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-avatar size="42" color="warning" class="mr-3">
                  <v-icon color="white">mdi-file-sign</v-icon>
                </v-avatar>
                <div class="text-caption grey--text text-uppercase font-weight-medium">
                  Total Contratado
                </div>
              </div>
              <div class="text-h5 font-weight-bold grey--text text--darken-3">
                {{ getTotalContratado | currency }}
              </div>
            </v-card-text>
            <div class="card-accent warning" />
          </v-card>
        </v-col>

        <!-- Economías -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg recursos-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-avatar size="42" :color="getEconomias >= 0 ? 'success' : 'error'" class="mr-3">
                  <v-icon color="white">
                    {{ getEconomias >= 0 ? 'mdi-piggy-bank' : 'mdi-alert' }}
                  </v-icon>
                </v-avatar>
                <div class="text-caption grey--text text-uppercase font-weight-medium">
                  Economías
                </div>
              </div>
              <div
                class="text-h5 font-weight-bold"
                :class="getEconomias >= 0 ? 'success--text' : 'error--text'">
                {{ getEconomias | currency }}
              </div>
            </v-card-text>
            <div class="card-accent" :class="getEconomias >= 0 ? 'success' : 'error'" />
          </v-card>
        </v-col>

        <!-- Porcentaje Economías -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg recursos-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-avatar size="42" color="grey darken-2" class="mr-3">
                  <v-icon color="white">mdi-percent</v-icon>
                </v-avatar>
                <div class="text-caption grey--text text-uppercase font-weight-medium">
                  Porcentaje Economías
                </div>
              </div>
              <div class="d-flex align-center">
                <span
                  class="text-h5 font-weight-bold mr-2"
                  :class="getEconomias >= 0 ? 'success--text' : 'error--text'">
                  {{ getPorcentajeEconomias.toFixed(2) }}%
                </span>
                <v-icon
                  :color="getEconomias >= 0 ? 'success' : 'error'"
                  size="20">
                  {{ getEconomias >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                </v-icon>
              </div>
            </v-card-text>
            <div class="card-accent grey darken-2" />
          </v-card>
        </v-col>
      </v-row>

      <!-- Detalle por obra -->
      <v-card class="rounded-lg" elevation="2">
        <v-card-title class="grey darken-3 white--text py-3 px-4">
          <v-icon left color="white" size="22">mdi-format-list-bulleted-square</v-icon>
          <span class="text-subtitle-1 font-weight-medium">
            Detalle de Recursos por Obra
          </span>
          <v-spacer />
          <v-chip small color="white" text-color="grey darken-3" class="font-weight-bold">
            {{ getContratoObras.length }}
            {{ getContratoObras.length === 1 ? 'obra' : 'obras' }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- Si no hay recursos -->
          <template v-if="getRecursosAplanados.length === 0">
            <div class="text-center py-12">
              <v-icon size="72" color="grey lighten-2">mdi-cash-remove</v-icon>
              <h3 class="text-h6 grey--text text--darken-1 mt-4 font-weight-medium">
                No hay recursos autorizados
              </h3>
              <p class="grey--text mt-2 mb-0">
                Las obras de este contrato no tienen recursos asignados
              </p>
            </div>
          </template>

          <!-- Lista de recursos por obra -->
          <template v-else>
            <div
              v-for="(obraData, index) in getRecursosPorObra"
              :key="obraData.id"
              class="obra-section"
              :class="{ 'border-top': index > 0 }">
              <!-- Header de obra -->
              <div class="obra-header pa-4">
                <v-row align="center" no-gutters>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <v-avatar size="40" color="grey darken-3" class="mr-3">
                        <v-icon color="white" size="20">mdi-domain</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold grey--text text--darken-3">
                          {{ obraData.clave }}
                        </div>
                        <div class="text-caption grey--text text-truncate"
                          style="max-width: 400px;">
                          {{ obraData.meta }}
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" class="mt-3 mt-md-0">
                    <div class="d-flex justify-md-end">
                      <div class="metric-box mr-3">
                        <div class="text-caption grey--text">Autorizado</div>
                        <div class="font-weight-bold primary--text">
                          {{ obraData.totalAutorizado | currency }}
                        </div>
                      </div>
                      <div class="metric-box mr-3">
                        <div class="text-caption grey--text">Contratado</div>
                        <div class="font-weight-bold warning--text">
                          {{ obraData.totalContratado | currency }}
                        </div>
                      </div>
                      <div class="metric-box">
                        <div class="text-caption grey--text">Economías</div>
                        <div class="d-flex align-center">
                          <span
                            class="font-weight-bold mr-1"
                            :class="obraData.economias >= 0 ? 'success--text' : 'error--text'">
                            {{ obraData.economias | currency }}
                          </span>
                          <v-chip
                            x-small
                            :color="obraData.economias >= 0 ? 'success' : 'error'"
                            text-color="white"
                            class="px-2">
                            {{ obraData.porcentajeEconomias.toFixed(1) }}%
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Tabla de recursos de la obra -->
              <v-simple-table dense class="recursos-table">
                <thead>
                  <tr>
                    <th class="text-left pl-6" width="35%">Fuente Financiera</th>
                    <th class="text-left" width="30%">Oficio de Autorización</th>
                    <th class="text-right" width="20%">Importe</th>
                    <th class="text-center pr-6" width="15%">% del Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="recurso in obraData.recursos" :key="recurso.id">
                    <td class="py-3 pl-6">
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-wallet</v-icon>
                        <span class="font-weight-medium">{{ recurso.fuenteFinanciera }}</span>
                      </div>
                    </td>
                    <td class="py-3">
                      <div class="d-flex align-center">
                        <v-icon small color="grey" class="mr-2">mdi-file-document</v-icon>
                        <span>{{ recurso.oficioNumero }}</span>
                      </div>
                    </td>
                    <td class="text-right py-3">
                      <span class="font-weight-bold text-body-1">
                        {{ recurso.importe | currency }}
                      </span>
                    </td>
                    <td class="text-center py-3 pr-6">
                      <v-chip
                        small
                        :color="getColorPorcentaje(recurso.porcentaje)"
                        text-color="white"
                        class="font-weight-medium">
                        {{ recurso.porcentaje.toFixed(1) }}%
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <!-- Total consolidado (si hay más de una obra) -->
            <div v-if="getContratoObras.length > 1" class="total-consolidado pa-4">
              <v-row align="center" no-gutters>
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <v-avatar size="40" color="grey darken-4" class="mr-3">
                      <v-icon color="white" size="20">mdi-sigma</v-icon>
                    </v-avatar>
                    <span class="text-subtitle-1 font-weight-bold white--text">
                      TOTAL CONSOLIDADO
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="mt-3 mt-md-0">
                  <div class="d-flex justify-md-end">
                    <div class="metric-box-dark mr-3">
                      <div class="text-caption white--text" style="opacity: 0.7;">
                        Autorizado
                      </div>
                      <div class="font-weight-bold white--text">
                        {{ getTotalRecursosAutorizados | currency }}
                      </div>
                    </div>
                    <div class="metric-box-dark mr-3">
                      <div class="text-caption white--text" style="opacity: 0.7;">
                        Contratado
                      </div>
                      <div class="font-weight-bold white--text">
                        {{ getTotalContratado | currency }}
                      </div>
                    </div>
                    <div class="metric-box-dark">
                      <div class="text-caption white--text" style="opacity: 0.7;">Economías</div>
                      <div class="d-flex align-center">
                        <span class="font-weight-bold white--text mr-1">
                          {{ getEconomias | currency }}
                        </span>
                        <v-chip
                          x-small
                          :color="getEconomias >= 0 ? 'success' : 'error'"
                          text-color="white"
                          class="px-2">
                          {{ getPorcentajeEconomias.toFixed(1) }}%
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import CONTRATO_OBRAS from '@/graphql/querys/administracion/contratos/contrato/contratoObras.gql';

export default {
  name: 'RecursosMain',
  data() {
    return {
      contratoObras: [],
    };
  },
  apollo: {
    contratoObras: {
      query: CONTRATO_OBRAS,
      variables() {
        return {
          idContrato: this.$route.params.idContrato,
        };
      },
    },
  },
  computed: {
    getContratoObras() {
      if (!this.contratoObras) return [];
      return this.contratoObras;
    },
    getTotalRecursosAutorizados() {
      return this.getContratoObras.reduce((total, contratoObra) => {
        const recursosObra = contratoObra.obra?.recursosAutorizados || [];
        const sumaRecursos = recursosObra.reduce((sum, r) => sum + (r.importe || 0), 0);
        return total + sumaRecursos;
      }, 0);
    },
    getTotalContratado() {
      return this.getContratoObras
        .reduce((total, contratoObra) => total + (contratoObra.total || 0), 0);
    },
    getEconomias() {
      return this.getTotalRecursosAutorizados - this.getTotalContratado;
    },
    getPorcentajeEconomias() {
      if (this.getTotalRecursosAutorizados === 0) return 0;
      return (this.getEconomias / this.getTotalRecursosAutorizados) * 100;
    },
    getRecursosAplanados() {
      const recursos = [];
      this.getContratoObras.forEach((contratoObra) => {
        const { obra } = contratoObra;
        if (!obra || !obra.recursosAutorizados) return;

        obra.recursosAutorizados.forEach((recurso) => {
          recursos.push({
            id: recurso.id,
            obraClave: obra.clave,
            obraMeta: obra.meta,
            fuenteFinanciera: recurso.fuenteFinanciera?.nombre || 'Sin fuente',
            oficioNumero: recurso.oficioAutorizacion?.numero || 'Sin oficio',
            importe: recurso.importe || 0,
          });
        });
      });
      return recursos;
    },
    getRecursosPorObra() {
      return this.getContratoObras.map((contratoObra) => {
        const { obra } = contratoObra;
        const recursosObra = obra?.recursosAutorizados || [];
        const totalAutorizado = recursosObra.reduce(
          (sum, r) => sum + (r.importe || 0), 0,
        );
        const totalContratado = contratoObra.total || 0;
        const economias = totalAutorizado - totalContratado;
        const porcentajeEconomias = totalAutorizado > 0
          ? (economias / totalAutorizado) * 100
          : 0;

        return {
          id: obra?.id,
          clave: obra?.clave,
          meta: obra?.meta,
          totalContratado,
          totalAutorizado,
          economias,
          porcentajeEconomias,
          recursos: recursosObra.map((r) => ({
            id: r.id,
            fuenteFinanciera: r.fuenteFinanciera?.nombre || 'Sin fuente',
            oficioNumero: r.oficioAutorizacion?.numero || 'Sin oficio',
            importe: r.importe || 0,
            porcentaje: totalAutorizado > 0
              ? ((r.importe || 0) / totalAutorizado) * 100
              : 0,
          })),
        };
      });
    },
    getResumenPorObra() {
      return this.getContratoObras.map((contratoObra) => {
        const { obra } = contratoObra;
        const recursosObra = obra?.recursosAutorizados || [];
        const recursosAutorizados = recursosObra.reduce(
          (sum, r) => sum + (r.importe || 0), 0,
        );
        const totalContratado = contratoObra.total || 0;
        const economias = recursosAutorizados - totalContratado;
        const porcentajeEconomias = recursosAutorizados > 0
          ? (economias / recursosAutorizados) * 100
          : 0;

        return {
          id: obra?.id,
          clave: obra?.clave,
          recursosAutorizados,
          totalContratado,
          economias,
          porcentajeEconomias,
        };
      });
    },
  },
  methods: {
    getColorPorcentaje(porcentaje) {
      if (porcentaje >= 50) return 'primary';
      if (porcentaje >= 25) return 'info';
      if (porcentaje >= 10) return 'warning';
      return 'grey';
    },
  },
};
</script>

<style lang="scss" scoped>
// Cards de métricas
.recursos-card {
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
  }
}

.card-accent {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
}

// Secciones de obra
.obra-section {
  &.border-top {
    border-top: 1px solid #e0e0e0;
  }
}

.obra-header {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-bottom: 1px solid #eeeeee;
}

// Cajas de métricas
.metric-box {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 120px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #bdbdbd;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.metric-box-dark {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 120px;
  text-align: center;
}

// Tabla de recursos
.recursos-table {
  thead {
    th {
      background: #fafafa !important;
      border-bottom: 2px solid #e0e0e0 !important;
      color: #616161 !important;
      font-size: 0.75rem !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  tbody {
    tr {
      transition: background-color 0.15s ease;

      &:hover {
        background-color: #f5f5f5 !important;
      }

      td {
        border-bottom: 1px solid #f5f5f5 !important;
      }
    }
  }
}

// Total consolidado
.total-consolidado {
  background: linear-gradient(135deg, #424242 0%, #212121 100%);
  border-top: 1px solid #757575;
}
</style>
